import React, { useEffect, useState } from "react";
import StatCard from "./StatCard";
import { Icon } from "@iconify/react";
import LoanCalculationCard, {
  LoanResult,
} from "../../components/LoanCalculationCard";
import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const [totalPayable, setTotalPayable] = useState(0);
  const [monthlyDue, setMonthlyDue] = useState(0);
  const [weeklyDue, setWeeklyDue] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [data, setData] = useState<any>(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Only run redirect logic after authentication status is known
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }

    // Redirect "Customer" role users to Profile
    if (user?.role === "Customer") {
      navigate("/profile", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axiosClient.get("/dashboard/summary");
        setData(response.data);
        // console.log(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  // Optionally render nothing (or a spinner) while redirecting
  if (!isAuthenticated || user?.role === "Customer") {
    return null;
  }

  const onCalculate = (res: LoanResult) => {
    setTotalPayable(res.totalPayable);
    setMonthlyDue(res.monthlyDue);
    setWeeklyDue(res.weeklyDue || 0);
    setTotalInterest(res.totalInterest || 0);
  };
  const currency = (v: number) =>
    v.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });
  const SkeletonStat: React.FC = () => (
    <div className="bg-white rounded-xl p-4 shadow-sm animate-pulse">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-4 w-full">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-6 bg-gray-200 rounded w-2/3 mt-2" />
        </div>
        <div className="w-12 h-12 rounded-lg bg-gray-200" />
      </div>
    </div>
  );
  const SkeletonResultRow: React.FC = () => (
    <div className="flex justify-between text-md font-medium rounded-md border-2 border-gray-100 bg-white px-3 py-2 shadow-sm animate-pulse">
      <span className="h-4 w-1/3 bg-gray-200 rounded" />
      <span className="h-4 w-24 bg-gray-200 rounded" />
    </div>
  );

  return (
    <div className="pt-3">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ">
        {loading ? (
          // show 4 skeletons while loading
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : (
          // actual stat cards
          <>
            <StatCard
              title="Active Loans"
              value={data?.total_loans || 0}
              icon={<Icon icon="octicon:graph-24" width={30} height={30} />}
              bg="#1d7754"
              iconbg="bg-green-100"
              iconcolor="text-green-500"
            />
            <StatCard
              title="Active Customers"
              value={data?.total_customers || 0}
              icon={
                <Icon
                  icon="fluent:person-star-24-regular"
                  width={30}
                  height={30}
                />
              }
              bg="#9253d0"
              iconbg="bg-blue-100"
              iconcolor="text-blue-500"
            />
            <StatCard
              title="Active Employees"
              value={data?.total_employees || 0}
              icon={<Icon icon="mdi:person-tie" width={30} height={30} />}
              bg="#ca3a62"
              iconbg="bg-orange-100"
              iconcolor="text-orange-500"
            />
            <StatCard
              title="Today’s Gross Collections"
              value={data?.today_collection || 0}
              icon={<Icon icon="tdesign:money" width={30} height={30} />}
              bg="#bd9416"
              iconbg="bg-orange-100"
              iconcolor="text-orange-500"
            />
          </>
        )}
      </div>

      {/* Loan + Result */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 light-bg-color p-6 rounded-2xl shadow-sm">
        <LoanCalculationCard onCalculate={onCalculate} />

        <div className="flex flex-col justify-start md:p-3 md:pt-0">
          <h3 className="text-2xl font-bold mb-6 text-color ">
            Calculation Results
          </h3>

          <div className="space-y-3">
            {loading ? (
              <>
                <SkeletonResultRow />
                <SkeletonResultRow />
                <SkeletonResultRow />
              </>
            ) : (
              <>
                <div className="group bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200 shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-blue-700 uppercase tracking-wide">
                      Total Payable
                    </span>
                    <span className="text-2xl font-bold text-blue-900">
                      {currency(totalPayable)}
                    </span>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200 shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-green-700 uppercase tracking-wide">
                      Total Interest
                    </span>
                    <span className="text-2xl font-bold text-green-900">
                      {currency(totalInterest)}
                    </span>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200 shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-red-700 uppercase tracking-wide">
                      Monthly Due
                    </span>
                    <span className="text-2xl font-bold text-red-900">
                      {currency(monthlyDue)}
                    </span>
                  </div>
                </div>

                {/* <div className="group bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-purple-700 uppercase tracking-wide">Weekly Due</span>
                <span className="text-2xl font-bold text-purple-900">{currency(weeklyDue)}</span>
              </div>
            </div> */}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
